import classnames from "classnames";
import React, { ClassAttributes, HTMLInputTypeAttribute, InputHTMLAttributes, LabelHTMLAttributes, SelectHTMLAttributes } from "react";

export type FormListItem = {
    title: string;
    value?: string | number,
    selected: boolean;
};

export type BaseFormInputState<T> = {
    value?: T,
    isValid: boolean,
    isTouched: boolean,
    errors: string[],
    rules?: Record<string, {
        value: string | number | boolean,
        error_message: string
    }>
}

export type BaseFormInputElement = {
    name: string,
    label?: string,
    placeholder?: string,
    formFloating?: boolean,
    isValid?: boolean,
    errors?: string[],
}

const Feedback: React.FunctionComponent<{
    isValid?: boolean,
    errors?: string[]
}> = (props) => {
   return (
        !props.isValid && props.errors && props.errors?.length > 0 ? (
           <div className={"invalid-feedback error"}>
               <ul className="list-unstyled">
                   {props.errors.map((x, i) => <li className="ps-2" key={i}>{x}</li>)}
                </ul>
            </div>
        ) : (
            <div className={"valid-feedback"}>Looks good!</div>
        )
    )
}

const Label: React.FunctionComponent<{
    id?: string,
    label?: string,
    input?: ClassAttributes<HTMLLabelElement> & LabelHTMLAttributes<HTMLLabelElement>
}> = (props) => {
    return (
        <label htmlFor={props.id} className="form-label" {...props.input}>
            {props.label}
        </label>
    )
}

const FormInput: React.FunctionComponent<BaseFormInputElement & {
    type?: HTMLInputTypeAttribute | undefined,
    checked?: boolean,
    value?: string | string[],
    input?: ClassAttributes<HTMLInputElement> & InputHTMLAttributes<HTMLInputElement>,
    onChange?: (
        event: React.ChangeEvent<HTMLInputElement>,
        data?: {
            value?: string;
            checked: boolean;
        }
    ) => void
}> = (props) => {
    return (
        <>
            {props.type === "checkbox" && (
                <>
                    <div className="form-check form-switch2">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id={props.name}
                            name={props.name}
                            value={props.value}
                            checked={props.checked ?? false}
                            {...props.input}
                            onChange={(e) => {
                                if (props.onChange) {
                                    props.onChange(e, {
                                        value: e.target.value,
                                        checked: e.target.checked,
                                    });
                                }
                            }}
                        />
                        <Label id={props.name} label={props.label} input={{ className: "form-check-label" }} />
                    </div>
                    <Feedback isValid={props.isValid} errors={props.errors} />
                </>
            )}

            {props.type === "radio" && (
                <>
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="radio"
                            id={props.name}
                            name={props.name}
                            value={props.value}
                            checked={props.checked ?? false}
                            {...props.input}
                            onChange={(e) => {
                                if (props.onChange) {
                                    props.onChange(e, {
                                        value: e.target.value,
                                        checked: e.target.checked,
                                    });
                                }
                            }}
                        />
                        <Label id={props.name} label={props.label} input={{ className: "form-check-label" }} />
                    </div>
                    <Feedback isValid={props.isValid} errors={props.errors} />
                </>
            )}

            {!(props.type === "checkbox" || props.type === "radio") && (
                <>
                    {props.label && !props.formFloating && (
                        <Label id={props.name} label={props.label} input={{ className: "form-label" }} />
                    )}

                    {props.type !== "textarea" && (
                        <input
                            className={`form-control ${!props.isValid ? "is-invalid" : ""}`}
                            {...props.input}
                            type={props.type}
                            id={props.name}
                            name={props.name}
                            placeholder={props.placeholder}
                            aria-label={props.formFloating ? props.label : ""}
                            value={props.value}
                            onChange={(e) => {
                                if (props.onChange) {
                                    props.onChange(e, {
                                        value: e.target.value,
                                        checked: e.target.checked,
                                    });
                                }
                            }}
                        />
                    )}

                    {props.label && props.formFloating && (
                        <Label id={props.name} label={props.label} input={{ className: "form-label" }} />
                    )}
                    <Feedback isValid={props.isValid} errors={props.errors} />
                </>
            )}
        </>
    );
};

export const FormTextAreaInput: React.FunctionComponent<BaseFormInputElement & {
    value?: string,
    input?: ClassAttributes<HTMLTextAreaElement> & InputHTMLAttributes<HTMLTextAreaElement>,
    onChange?: (
        event: React.ChangeEvent<
            HTMLTextAreaElement
        >,
        data?: {
            value?: string;
            checked: boolean;
        }
    ) => void
}> = (props) => {
    return (
        <>
            {props.label && !props.formFloating && (
                <Label id={props.name} label={props.label} input={{ className: "form-label" }} />
            )}

            <textarea
                className={`form-control ${props.isValid ? "" : "is-invalid"}`}
                {...props.input}
                id={props.name}
                name={props.name}
                placeholder={props.placeholder}
                aria-label={props.formFloating ? props.label : ""}
                style={{ height: "100px" }}
                value={props.value}
                onChange={(e) => {
                    if (props.onChange) {
                        props.onChange(e, {
                            value: e.target.value,
                            checked: false,
                        });
                    }
                }}>
            </textarea>

            {props.label && props.formFloating && (
                <Label id={props.name} label={props.label} input={{ className: "form-label" }} />
            )}

            <Feedback isValid={props.isValid} errors={props.errors} />
        </>
    )
}

export const FormCheckBoxListInput: React.FunctionComponent<BaseFormInputElement & {
    inline?: boolean,
    value?: string | string[],
    dataSource: FormListItem[],
    input?: ClassAttributes<HTMLInputElement> & InputHTMLAttributes<HTMLInputElement>,
    onChange?: (
        event: React.ChangeEvent<HTMLInputElement>,
        data?: FormListItem[]
    ) => void
}> = (props) => {
    return (
        <>
            {
                props.dataSource.map((x, i) => {
                    let selected = x.selected;
                    let value = x.value ?? x.title;

                    if (props.value) {
                        if (!Array.isArray(props.value)) {
                            if (value === props.value) selected = true
                        } else {
                            let element = props.value.find(x => x === value);
                            if (element) selected = true;
                        }
                    }

                    return (
                        <div className={classnames("form-check", { "form-check-inline": props.inline })} key={`${props.name}_${i}`}>
                            <input
                                className="form-check-input"
                                {...props.input}
                                type="checkbox"
                                id={`${props.name}_${i}`}
                                name={`${props.name}[]`}
                                value={value}
                                checked={selected}
                                onChange={(e) => {
                                    const ds = props.dataSource.map((x) => {
                                        let value = x.value ?? x.title;

                                        if (props.value) {
                                            if (!Array.isArray(props.value)) {
                                                if (value === props.value) x.selected = true;
                                            } else {
                                                let element = props.value.find(x => x === value);
                                                if (element) x.selected = true;
                                            }
                                        }

                                        if (value === e.target.value) {
                                            x.selected = e.target.checked;
                                        }

                                        return x;
                                    })

                                    if (props.onChange) props.onChange(e, [...ds]);
                                }}
                            />
                            <Label id={`${props.name}_${i}`} label={x.title ?? x.value} input={{ className: "form-check-label" }} />
                        </div>
                    )
                })
            }

            <Feedback isValid={props.isValid} errors={props.errors} />
        </>
    )
}

export const FormRadioBoxListInput: React.FunctionComponent<BaseFormInputElement & {
    value?: string,
    dataSource: FormListItem[],
    inline?: boolean,
    input?: ClassAttributes<HTMLInputElement> & InputHTMLAttributes<HTMLInputElement>,
    onChange?: (
        event: React.ChangeEvent<HTMLInputElement>,
        data?: FormListItem[]
    ) => void
}> = (props) => {
    return (
        <>
            {
                props.dataSource.map((x, i) => {
                    let selected = x.selected;
                    let value = x.value ?? x.title;
                    if (props.value && !Array.isArray(props.value)) {
                        if (value === props.value) {
                            selected = true
                        }
                    } else if (props.value && Array.isArray(props.value)) {
                        let element = props.value.find(x => x === value);
                        if (element) {
                            selected = true
                        }
                    }

                    return (
                        <div className={classnames("form-check", { "form-check-inline": props.inline })} key={`${props.name}_${i}`}>
                            <input
                                className="form-check-input"
                                {...props.input}
                                type="radio"
                                id={`${props.name}_${i}`}
                                name={`${props.name}[]`}
                                value={value}
                                checked={selected}
                                onChange={(e) => {
                                    const dataSource = props.dataSource.map((x) => {
                                        x.selected = false;
                                        if ((x.value ?? x.title) === e.target.value) {
                                            x.selected = true;
                                        }
                                        return x
                                    })

                                    if (props.onChange) props.onChange(e, dataSource);
                                }}
                            />
                            <Label id={`${props.name}_${i}`} label={x.title ?? x.value} input={{ className: "form-check-label" }} />
                        </div>
                    )
                })
            }
            <Feedback isValid={props.isValid} errors={props.errors} />
        </>
    )

}

export const FormSelectInput: React.FunctionComponent<BaseFormInputElement & {
    value?: string;
    dataSource: FormListItem[];
    defaultOptionText?: string;
    input?: ClassAttributes<HTMLSelectElement> & SelectHTMLAttributes<HTMLSelectElement>;
    onChange?: (
        event: React.ChangeEvent<HTMLSelectElement>,
        data?: FormListItem[]
    ) => void;
}> = (props) => {
    return (
        <>
            {props.label && !props.formFloating && (
                <Label id={props.name} label={props.label} input={{ className: "form-label" }} />
            )}

            <select
                className={`form-select ${!props.isValid ? "is-invalid" : ""}`}
                {...props.input}
                id={props.name}
                name={props.name}
                value={props.value}
                onChange={(e) => {
                    if (props.onChange) props.onChange(e, []);
                }}>
                <option key={`${props.name}_${1}`} value="">{props.defaultOptionText ? props.defaultOptionText : "Choose"}</option>
                {
                    props.dataSource.map((x, i) => {
                        return <option key={`${props.name}_${i + 2}`} value={x.value ?? x.title}>{x.title}</option>
                    })
                }
            </select>

            {props.label && props.formFloating && (
                <Label id={props.name} label={props.label} input={{ className: "form-label" }} />
            )}

            <Feedback isValid={props.isValid} errors={props.errors} />
        </>
    )
}

export const FormListViewInput: React.FunctionComponent<BaseFormInputElement & {
    value?: string | string[],
    dataSource: FormListItem[],
    defaultOptionText?: string,
    input?: ClassAttributes<HTMLSelectElement> & SelectHTMLAttributes<HTMLSelectElement>,
    minItem?: number,
    maxItem?: number,
    onChange?: (
        event: React.ChangeEvent<HTMLSelectElement>,
        data?: FormListItem[]
    ) => void
}> = (props) => {
    return (
        <>
            {props.label && !props.formFloating && (
                <Label id={props.name} label={props.label} />
            )}

            <select
                className={`form-select ${!props.isValid ? "is-invalid" : ""}`}
                {...props.input}
                id={props.name}
                name={props.name}
                style={{ height: "100px" }}
                value={props.value}
                multiple={true}
                onChange={(e) => {
                    let length = e.target.selectedOptions.length;

                    const ds = props.dataSource.map(x => {
                        x.selected = false;
                        return x;
                    })

                    for (let index = 0; index < length; index++) {
                        const element = e.target.selectedOptions[index];

                        if (element.selected) {
                            let foundElement = ds.find(x => (x.value ?? x.title) === element.value)
                            if (foundElement) {
                                foundElement.selected = element.selected;
                            }
                        }
                    }

                    if (props.onChange) props.onChange(e, ds);
                }}>
                <option key={`${props.name}_${1}`}> {props.defaultOptionText ? props.defaultOptionText : "Choose"}</option>
                {
                    props.dataSource.map((x, i) => {
                        return <option key={`${props.name}_${i + 2}`} value={x.value ?? x.title}>{x.title}</option>
                    })
                }
            </select>

            {props.label && props.formFloating && (
                <Label id={props.name} label={props.label} input={{ className: "form-label" }} />
            )}

            <Feedback isValid={props.isValid} errors={props.errors} />
        </>
    )
}

export default FormInput;
